import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Tooltip,
  Typography,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
  useTheme,
  alpha
} from '@mui/material';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddBoxIcon from '@mui/icons-material/AddBox';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PendingIcon from '@mui/icons-material/Pending';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SendTimeExtensionIcon from '@mui/icons-material/SendTimeExtension';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import HighlightIcon from '@mui/icons-material/Highlight';
import CodeIcon from '@mui/icons-material/Code';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import TitleIcon from '@mui/icons-material/Title';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import TextFormatIcon from '@mui/icons-material/TextFormat';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import Looks3Icon from '@mui/icons-material/Looks3';
import Looks4Icon from '@mui/icons-material/Looks4';
import Looks5Icon from '@mui/icons-material/Looks5';
import ImageIcon from '@mui/icons-material/Image';
import TableChartIcon from '@mui/icons-material/TableChart';
import HomeIcon from '@mui/icons-material/Home';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import BorderAllIcon from '@mui/icons-material/BorderAll';
import BuildIcon from '@mui/icons-material/Build';
import DescriptionIcon from '@mui/icons-material/Description';
import GetAppIcon from '@mui/icons-material/GetApp';
import MenuIcon from '@mui/icons-material/Menu';
import PageIcon from '@mui/icons-material/Pages';
import CropPortraitIcon from '@mui/icons-material/CropPortrait';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';

import BackButton from '../../../../components/BackButton';
import ContractLocationBreadcrumb from '../../components/ContractLocationBreadcrumb';
import { getToolbarWidth, windowWidth } from '../../../../utils/utils';
import AiToolbarButton from './AiToolbarButton';
import SuggestModeButton from '../components/suggestions/SuggestModeButton';

// Import required utility functions
import { setFontSize, setFontFamily, getCurrentFontSize, getCurrentFontFamily } from '../extensions/CustomFont';
import { setTextColor, getCurrentTextColor, setupFormattingDetection } from '../extensions/CustomColor';
import { backendClient } from '../../../../api/backend';
import { updateContractStatus, updateContractCountry } from '../../../../redux/actions/contractAction';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { setTableCellBackground, getTableCellBackground } from '../extensions/CustomTable';
import CountryDropdown from '../../../../components/common/CountryDropdown';
import PageSetupPanel from './PageSetupPanel';


const TableIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 5C4 4.44772 4.44772 4 5 4H19C19.5523 4 20 4.44772 20 5V19C20 19.5523 19.5523 20 19 20H5C4.44772 20 4 19.5523 4 19V5Z" stroke="currentColor" strokeWidth="2" />
    <path d="M4 9H20" stroke="currentColor" strokeWidth="2" />
    <path d="M4 14H20" stroke="currentColor" strokeWidth="2" />
    <path d="M9 4V20" stroke="currentColor" strokeWidth="2" />
    <path d="M14 4V20" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const ColumnBeforeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 18L4 12L7 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="10" y="4" width="10" height="16" rx="1" stroke="currentColor" strokeWidth="2" />
    <line x1="15" y1="4" x2="15" y2="20" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const ColumnAfterIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 6L20 12L17 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="4" y="4" width="10" height="16" rx="1" stroke="currentColor" strokeWidth="2" />
    <line x1="9" y1="4" x2="9" y2="20" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const RowAboveIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 7L12 4L18 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="4" y="10" width="16" height="10" rx="1" stroke="currentColor" strokeWidth="2" />
    <line x1="4" y1="15" x2="20" y2="15" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const RowBelowIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 17L12 20L18 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="4" y="4" width="16" height="10" rx="1" stroke="currentColor" strokeWidth="2" />
    <line x1="4" y1="9" x2="20" y2="9" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const DeleteRowIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="4" width="16" height="16" rx="1" stroke="currentColor" strokeWidth="2" />
    <line x1="4" y1="12" x2="20" y2="12" stroke="currentColor" strokeWidth="2" />
    <path d="M8 8L16 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M16 8L8 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const DeleteColumnIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="4" width="16" height="16" rx="1" stroke="currentColor" strokeWidth="2" />
    <line x1="12" y1="4" x2="12" y2="20" stroke="currentColor" strokeWidth="2" />
    <path d="M8 8L16 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M16 8L8 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Normalize legacy French status strings to canonical enum values
const normalizeStatus = (s) => {
  const aliases = {
    'brouillon': 'DRAFT',
    'en revu': 'IN_REVIEW',
    'en attente de signature': 'PENDING_SIGNATURE',
    'signé': 'SIGNED',
  };
  return aliases[s] || s;
};

const ContractStatusSubmenu = ({ onStatusChange, currentStatus, isLocked }) => {
  const { t } = useTranslation();
  const normalized = normalizeStatus(currentStatus);
  return (
    <Box sx={{ minWidth: 200 }}>
      <MenuItem
        onClick={() => onStatusChange('DRAFT')}
        disabled={isLocked}
        selected={normalized === 'DRAFT'}
        sx={{
          py: 1,
          '&.Mui-selected': {
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': { bgcolor: 'primary.main' },
            '& .MuiListItemIcon-root': { color: 'white' },
          },
        }}
      >
        <ListItemIcon><EditNoteIcon fontSize="small" /></ListItemIcon>
        <ListItemText primary={t('contractStatus.DRAFT')} />
      </MenuItem>
      <MenuItem
        onClick={() => onStatusChange('IN_REVIEW')}
        disabled={isLocked}
        selected={normalized === 'IN_REVIEW'}
        sx={{
          py: 1,
          '&.Mui-selected': {
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': { bgcolor: 'primary.main' },
            '& .MuiListItemIcon-root': { color: 'white' },
          },
        }}
      >
        <ListItemIcon><PendingIcon fontSize="small" /></ListItemIcon>
        <ListItemText primary={t('contractStatus.IN_REVIEW')} />
      </MenuItem>
      <Divider />
      <MenuItem
        disabled
        sx={{
          py: 1,
          opacity: 1,
          bgcolor: normalized === 'PENDING_SIGNATURE' ? 'primary.main' : 'transparent',
          color: normalized === 'PENDING_SIGNATURE' ? 'white' : 'text.primary',
          '& .MuiListItemIcon-root': { color: normalized === 'PENDING_SIGNATURE' ? 'white' : 'inherit' },
        }}
      >
        <ListItemIcon><SendTimeExtensionIcon fontSize="small" /></ListItemIcon>
        <ListItemText primary={t('contractStatus.PENDING_SIGNATURE')} />
      </MenuItem>
      <MenuItem
        disabled
        sx={{
          py: 1,
          opacity: 1,
          bgcolor: normalized === 'SIGNED' ? 'primary.main' : 'transparent',
          color: normalized === 'SIGNED' ? 'white' : 'text.primary',
          '& .MuiListItemIcon-root': { color: normalized === 'SIGNED' ? 'white' : 'inherit' },
        }}
      >
        <ListItemIcon><AssignmentTurnedInIcon fontSize="small" /></ListItemIcon>
        <ListItemText primary={t('contractStatus.SIGNED')} />
      </MenuItem>
    </Box>
  );
};

const ToolbarPlugin = ({ editor, socketRef, contractId, saveContract, contractTitle, contractWorkspaceId, contractFolderId, contractWorkspaceName, contractFolderName, onContractMoved, selectedImageNode, hasUnsavedChanges, aiButtonRef, onSendComment, suggestModeActive: suggestModeActiveProp, suggestModeLocked, onSuggestModeChange, pageSettings, onPageSettingsChange, onAfterUndoRedo, mode = 'contract' }) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const currentUser = useSelector(state => state.auth.user.user);

  // State for menu
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('Home');
  const [anchorEl, setAnchorEl] = useState(null);
  const contractStatus = useSelector(state => mode === 'contract' ? state?.contract?.contract?.status : null)
  const contractCountry = useSelector(state => mode === 'contract' ? state?.contract?.contract?.country : null)
  const loadingStatus = useSelector(state => mode === 'contract' ? state?.contract?.loadingStatus : false)
  const isLocked = mode === 'template' ? false : ['PENDING_SIGNATURE', 'SIGNED'].includes(contractStatus)
  // Reviewers: toolbar formatting actions are disabled (they can only suggest)
  const isToolbarDisabled = mode === 'template' ? false : (isLocked || suggestModeLocked);

  // Suggest mode: controlled by parent via props so toolbar stays in sync with the
  // hook state (e.g. when the SuggestModeToast is dismissed externally).
  const suggestModeActive = suggestModeActiveProp ?? false;

  // Track current formatting
  const [currentTextColor, setCurrentTextColor] = useState('#000000');
  const [currentFontSize, setCurrentFontSize] = useState('16px');
  const [currentFontFamily, setCurrentFontFamily] = useState('Arial, sans-serif');

  // Reference for image input and menu button
  const imageInputRef = useRef(null);
  const menuButtonRef = useRef(null);
  // Add these refs to the component
  const toolbarScrollRef = useRef(null);

  // Page setup dialog state
  const [pageSetupOpen, setPageSetupOpen] = useState(false);

  // Submenu state
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null);
  const [currentSubmenu, setCurrentSubmenu] = useState(null);

  // Add color options — swap black ↔ white in dark mode
  const isDarkMode = theme.palette.mode === 'dark';
  const colorOptions = [
    isDarkMode
      ? { name: 'White', value: '#FFFFFF' }
      : { name: 'Black', value: '#000000' },
    { name: 'Red', value: '#FF0000' },
    { name: 'Green', value: '#008000' },
    { name: 'Blue', value: '#0000FF' },
    { name: 'Purple', value: '#800080' },
    { name: 'Orange', value: '#FFA500' },
    { name: 'Gray', value: '#808080' },
    { name: 'Brown', value: '#A52A2A' },
  ];

  // Add color options for table cells
  const tableCellColorOptions = [
    { name: 'No Color', value: 'transparent' },
    { name: 'Light Gray', value: '#f5f5f5' },
    { name: 'Light Blue', value: '#e3f2fd' },
    { name: 'Light Green', value: '#e8f5e9' },
    { name: 'Light Yellow', value: '#fffde7' },
    { name: 'Light Red', value: '#ffebee' },
    { name: 'Light Purple', value: '#f3e5f5' },
    { name: 'Light Orange', value: '#fff3e0' },
  ];

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const [tableDialogOpen, setTableDialogOpen] = useState(false);
  const [selectedCells, setSelectedCells] = useState({ rows: 1, cols: 1 });
  const [hoveredCell, setHoveredCell] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const tableButtonRef = useRef(null);

  // Set up formatting detection
  useEffect(() => {
    if (!editor) return;

    const cleanup = setupFormattingDetection(editor, {
      setCurrentTextColor,
      setCurrentFontSize,
      setCurrentFontFamily
    });

    return cleanup;
  }, [editor]);

  // Add this function to handle scrolling
  const handleToolbarScroll = (direction) => {
    if (!toolbarScrollRef.current) return;

    const scrollAmount = 150; // Adjust this value as needed
    const currentScroll = toolbarScrollRef.current.scrollLeft;

    if (direction === 'left') {
      toolbarScrollRef.current.scrollTo({
        left: Math.max(0, currentScroll - scrollAmount),
        behavior: 'smooth'
      });
    } else {
      toolbarScrollRef.current.scrollTo({
        left: currentScroll + scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleContractStatusChange = (status) => {
    if (!editor || status === normalizeStatus(contractStatus)) {
      handleSubmenuClose();
      return;
    }
    handleSubmenuClose();
    const HtmlContent = editor.getHTML()
    dispatch(updateContractStatus(contractId, status, HtmlContent))
      .catch((error) => {
        const msg = error?.response?.data?.detail || error?.message || t('contract_status_update_failed')
        enqueueSnackbar(msg, { variant: 'error' })
      })
  };

  // Add this function to check scroll possibilities
  const checkScrollability = () => {
    if (!toolbarScrollRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = toolbarScrollRef.current;

    // Can scroll left if we're not at the beginning
    setCanScrollLeft(scrollLeft > 0);

    // Can scroll right if there's more content to the right
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth);
  };

  // Add this scroll event handler
  const handleScroll = () => {
    checkScrollability();
  };

  // Add this useEffect to check scrollability on render and window resize
  useEffect(() => {
    if (!toolbarScrollRef.current) return;

    // Initial check
    checkScrollability();

    // Set up scroll event listener
    toolbarScrollRef.current.addEventListener('scroll', handleScroll);

    // Set up resize observer to check when size changes
    const resizeObserver = new ResizeObserver(() => {
      checkScrollability();
    });

    resizeObserver.observe(toolbarScrollRef.current);

    return () => {
      if (toolbarScrollRef.current) {
        toolbarScrollRef.current.removeEventListener('scroll', handleScroll);
        resizeObserver.unobserve(toolbarScrollRef.current);
      }
    };
  }, [toolbarScrollRef.current])

  // Safe handlers for undo/redo
  const handleUndo = () => {
    if (!editor) return;

    try {
      console.log('[Toolbar] handleUndo called');
      editor.chain().focus().undo().run();
      console.log('[Toolbar] undo executed, calling onAfterUndoRedo:', !!onAfterUndoRedo);
      onAfterUndoRedo?.();
    } catch (error) {
      console.error("Undo error:", error);
    }
  };

  const handleRedo = () => {
    if (!editor) return;

    try {
      console.log('[Toolbar] handleRedo called');
      editor.chain().focus().redo().run();
      console.log('[Toolbar] redo executed, calling onAfterUndoRedo:', !!onAfterUndoRedo);
      onAfterUndoRedo?.();
    } catch (error) {
      console.error("Redo error:", error);
    }
  };

  const handleSave = (e) => {
    e.preventDefault();
    saveContract();
  };

  // Menu handlers
  const handleMenuButtonClick = (e) => {
    setAnchorEl(menuButtonRef.current);
    setMenuOpen(prev => !prev);
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
  };

  const handleMenuItemClick = (menuItem) => {
    setActiveMenu(menuItem);
    setMenuOpen(false);
  };

  // Submenu handlers
  const handleSubmenuOpen = (event, menuName) => {
    setSubmenuAnchorEl(event.currentTarget);
    setCurrentSubmenu(menuName);
  };

  const handleSubmenuClose = () => {
    setSubmenuAnchorEl(null);
    setCurrentSubmenu(null);
  };

  // Apply heading
  const setHeading = (level) => {
    editor.chain().focus().toggleHeading({ level }).run();
    handleSubmenuClose();
  };

  // Apply text color
  const applyTextColor = (color) => {
    const result = setTextColor(editor, color);
    if (result) {
      setCurrentTextColor(result);
    }
    handleSubmenuClose();
  };

  // Apply typography style
  const applyTypographyStyle = (fontSize, fontFamily) => {
    if (fontSize) {
      setFontSize(editor, fontSize);
      setCurrentFontSize(fontSize);
    }

    if (fontFamily) {
      setFontFamily(editor, fontFamily);
      setCurrentFontFamily(fontFamily);
    }

    handleSubmenuClose();
  };

  // Handle image upload
  const handleImageUpload = (event) => {
    const files = event.target.files;

    if (files && files.length > 0) {
      const file = files[0];
      const reader = new FileReader();

      reader.onload = (e) => {
        const src = e.target.result;
        const img = new Image();

        img.onload = () => {
          // Cap initial width to 600px, maintain aspect ratio
          const maxWidth = 600;
          const naturalW = img.naturalWidth;
          const naturalH = img.naturalHeight;
          const insertWidth = naturalW > maxWidth ? maxWidth : naturalW;
          const insertHeight = Math.round(insertWidth * (naturalH / naturalW));

          editor.chain().focus().insertContent({
            type: 'resizableImage',
            attrs: {
              src,
              alt: file.name,
              title: file.name,
              width: `${insertWidth}px`,
              height: `${insertHeight}px`,
              originalWidth: naturalW,
              originalHeight: naturalH
            }
          }).run();
        };

        img.src = src;
      };

      reader.readAsDataURL(file);
      event.target.value = '';
    }
  };

  // Table operations
  const handleInsertTable = (event) => {
    setMousePosition({
      x: event.clientX,
      y: event.clientY
    });
    setTableDialogOpen(true);
  };

  // Get comments count
  const getCommentsCount = () => {
    if (!editor) return 0;
    const comments = editor.storage.comments?.comments || [];
    return comments.filter(c => !c.resolved).length;
  };

  const handleSuggestModeToggle = () => {
    // Notify parent — parent calls suggestionHook.setSuggestMode which in turn
    // calls editor.commands.setSuggestMode and updates suggestModeRef.
    onSuggestModeChange?.(!suggestModeActive);
  };

  // Add this function to handle table grid selection
  const handleTableGridSelection = (row, col) => {
    // Ensure minimum of 1x1
    setSelectedCells({ 
      rows: Math.max(1, row + 1), 
      cols: Math.max(1, col + 1) 
    });
  };

  const handleTableDialogClose = () => {
    setTableDialogOpen(false);
    setHoveredCell(null);
  };

  const handleTableDialogConfirm = () => {
    editor.chain().focus().insertTable({ 
      rows: selectedCells.rows, 
      cols: selectedCells.cols, 
      withHeaderRow: true 
    }).run();
    handleTableDialogClose();
  };

  // Add the TableGridSelection component
  const TableGridSelection = () => {
    const maxRows = 10;
    const maxCols = 10;

    const handleGridClick = (rowIndex, colIndex) => {
      const rows = Math.max(1, rowIndex + 1);
      const cols = Math.max(1, colIndex + 1);
      
      editor.chain().focus().insertTable({ 
        rows, 
        cols, 
        withHeaderRow: true 
      }).run();
      
      handleTableDialogClose();
    };

    return (
      <Box sx={{ p: 1.5 }}>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: `repeat(${maxCols}, 1fr)`,
          gap: 0.25,
          mb: 1.5
        }}>
          {Array.from({ length: maxRows }).map((_, rowIndex) => (
            Array.from({ length: maxCols }).map((_, colIndex) => {
              const isHovered = hoveredCell && 
                rowIndex <= hoveredCell.row && 
                colIndex <= hoveredCell.col;

              return (
                <Box
                  key={`${rowIndex}-${colIndex}`}
                  sx={{
                    width: 20,
                    height: 20,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: isHovered ? 'primary.main' : 'transparent',
                    opacity: isHovered ? 0.3 : 1,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'primary.main',
                      opacity: 0.3
                    }
                  }}
                  onMouseEnter={() => setHoveredCell({ row: rowIndex, col: colIndex })}
                  onMouseLeave={() => setHoveredCell(null)}
                  onClick={() => handleGridClick(rowIndex, colIndex)}
                />
              );
            })
          ))}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          {hoveredCell ? `${hoveredCell.row + 1} × ${hoveredCell.col + 1} table` : 'Select table size'}
        </Typography>
      </Box>
    );
  };

  // Add heading submenu component
  const HeadingSubmenu = () => (
    <Box sx={{ p: 1 }}>
      <ButtonGroup orientation="vertical" size="small" fullWidth>
        <Button
          onClick={() => setHeading(1)}
          startIcon={<LooksOneIcon />}
          sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
        >
          {t('heading1')}
        </Button>
        <Button
          onClick={() => setHeading(2)}
          startIcon={<LooksTwoIcon />}
          sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
        >
          {t('heading2')}
        </Button>
        <Button
          onClick={() => setHeading(3)}
          startIcon={<Looks3Icon />}
          sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
        >
          {t('heading3')}
        </Button>
        <Button
          onClick={() => setHeading(4)}
          startIcon={<Looks4Icon />}
          sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
        >
          {t('heading4')}
        </Button>
        <Button
          onClick={() => setHeading(5)}
          startIcon={<Looks5Icon />}
          sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
        >
          {t('heading5')}
        </Button>
        <Button
          onClick={() => editor.chain().focus().setParagraph().run()}
          sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
        >
          {t('paragraph')}
        </Button>
      </ButtonGroup>
    </Box>
  );

  // Add text color submenu component
  const TextColorSubmenu = () => (
    <Box sx={{ p: 1 }}>
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(4, 1fr)', 
        gap: 1,
        width: '200px'
      }}>
        {colorOptions.map((color) => (
          <Tooltip key={color.value} title={color.name}>
            <Box
              onClick={() => applyTextColor(color.value)}
              sx={{
                width: 24,
                height: 24,
                bgcolor: color.value,
                borderRadius: '4px',
                cursor: 'pointer',
                border: `1px solid ${theme.palette.divider}`,
                '&:hover': {
                  transform: 'scale(1.1)',
                  transition: 'transform 0.2s'
                }
              }}
            />
          </Tooltip>
        ))}
      </Box>
    </Box>
  );

  // Add TableCellColorSubmenu component
  const TableCellColorSubmenu = () => {
    const currentCellColor = getTableCellBackground(editor);
    
    return (
      <Box sx={{ p: 1 }}>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(4, 1fr)', 
          gap: 1,
          width: '200px'
        }}>
          {tableCellColorOptions.map((color) => (
            <Tooltip key={color.value} title={color.name}>
              <Box
                onClick={() => {
                  if (editor.isActive('tableCell')) {
                    setTableCellBackground(editor, color.value);
                  }
                  handleSubmenuClose();
                }}
                sx={{
                  width: 24,
                  height: 24,
                  bgcolor: color.value,
                  borderRadius: '4px',
                  cursor: 'pointer',
                  border: `1px solid ${theme.palette.divider}`,
                  position: 'relative',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    transition: 'transform 0.2s'
                  },
                  ...(currentCellColor === color.value && {
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: -2,
                      left: -2,
                      right: -2,
                      bottom: -2,
                      border: `2px solid ${theme.palette.primary.main}`,
                      borderRadius: '6px'
                    }
                  })
                }}
              />
            </Tooltip>
          ))}
        </Box>
      </Box>
    );
  };

  // Render toolbar based on active menu
  const renderToolbar = () => {
    if (!editor) return null;

    switch (activeMenu) {
      case 'Home':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
            {canScrollLeft && (
              <IconButton
                size="small"
                onClick={() => handleToolbarScroll('left')}
                sx={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                  bgcolor: alpha(theme.palette.background.paper, 0.8),
                  '&:hover': { bgcolor: theme.palette.action.hover }
                }}
              >
                <ArrowBackIosNewIcon fontSize="small" />
              </IconButton>
            )}
            <Box
              ref={toolbarScrollRef}
              sx={{
                display: 'flex',
                gap: 1,
                overflowX: 'auto',
                overflowY: 'hidden',
                px: 1,
                alignItems: 'center',
                height: '100%',
                width: getToolbarWidth(canScrollLeft, canScrollRight),
                flexWrap: 'nowrap',
                msOverflowStyle: 'none', /* for Internet Explorer, Edge */
                scrollbarWidth: 'none',  /* for Firefox */
                '&::-webkit-scrollbar': {
                  display: 'none'  /* for Chrome, Safari, and Opera */
                }
              }}>
              {/* Text Formatting */}
              <ButtonGroup size="small" variant="text" sx={{ mr: 0.5 }}>
                <Tooltip title={t('toolBold')}>
                  <Button
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    color={editor.isActive('bold') ? 'primary' : 'default'}
                    sx={{ px: 1 }}
                  >
                    <FormatBoldIcon fontSize="small" />
                  </Button>
                </Tooltip>
                <Tooltip title={t('toolItalic')}>
                  <Button
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    color={editor.isActive('italic') ? 'primary' : 'default'}
                    sx={{ px: 1 }}
                  >
                    <FormatItalicIcon fontSize="small" />
                  </Button>
                </Tooltip>
                <Tooltip title={t('toolUnderline')}>
                  <Button
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    color={editor.isActive('underline') ? 'primary' : 'default'}
                    sx={{ px: 1 }}
                  >
                    <FormatUnderlinedIcon fontSize="small" />
                  </Button>
                </Tooltip>
                <Tooltip title={t('toolStrike')}>
                  <Button
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    color={editor.isActive('strike') ? 'primary' : 'default'}
                    sx={{ px: 1 }}
                  >
                    <FormatStrikethroughIcon fontSize="small" />
                  </Button>
                </Tooltip>
              </ButtonGroup>

              <Divider orientation="vertical" flexItem />

              {/* Heading Dropdown */}
              <ButtonGroup size="small" variant="text" sx={{ mr: 0.5 }}>
                <Button
                  onClick={(e) => handleSubmenuOpen(e, 'heading')}
                  color={(editor.isActive('heading') || editor.isActive('paragraph')) ? 'primary' : 'default'}
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                  startIcon={<TitleIcon fontSize="small" />}
                  endIcon={<ArrowDropDownIcon fontSize="small" />}
                >
                  {editor.isActive('paragraph') ? 'Texte' :
                    editor.isActive({ level: 1 }) ? 'Heading 1' :
                      editor.isActive({ level: 2 }) ? 'Heading 2' :
                        editor.isActive({ level: 3 }) ? 'Heading 3' :
                          editor.isActive({ level: 4 }) ? 'Heading 4' :
                            editor.isActive({ level: 5 }) ? 'Heading 5' :
                              'Heading'}
                </Button>
              </ButtonGroup>

              <Divider orientation="vertical" flexItem />

              {/* Text Color */}
              <ButtonGroup size="small" variant="text" sx={{ mr: 0.5 }}>
                <Button
                  onClick={(e) => handleSubmenuOpen(e, 'textColor')}
                  sx={{
                    textTransform: 'none',
                    pr: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <TextFormatIcon fontSize="small" />
                    <Box sx={{ width: 14, height: 3, borderRadius: '1px', bgcolor: (isDarkMode && (currentTextColor === '#000000' || currentTextColor === '#000')) ? '#FFFFFF' : currentTextColor, mt: '-2px' }} />
                  </Box>
                </Button>

                <Tooltip title="Highlight">
                  <Button
                    onClick={() => editor.chain().focus().toggleHighlight().run()}
                    color={editor.isActive('highlight') ? 'primary' : 'default'}
                    sx={{ pl: 1 }}
                  >
                    <HighlightIcon fontSize="small" />
                  </Button>
                </Tooltip>
              </ButtonGroup>

              <Divider orientation="vertical" flexItem />

              {/* Text Size Controls */}
              <ButtonGroup size="small" variant="text" sx={{ mr: 0.5 }}>
                <Tooltip title="Decrease Font Size">
                  <Button
                    onClick={() => {
                      const currentSize = parseInt(getCurrentFontSize(editor).replace('px', ''));
                      const newSize = Math.max(8, currentSize - 2) + 'px';
                      applyTypographyStyle(newSize);
                    }}
                    sx={{ px: 1 }}
                  >
                    <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>A-</Typography>
                  </Button>
                </Tooltip>

                <Button
                  onClick={(e) => handleSubmenuOpen(e, 'fontSize')}
                  sx={{
                    textTransform: 'none',
                    minWidth: '50px'
                  }}
                >
                  {currentFontSize.replace('px', '')}
                </Button>

                <Tooltip title="Increase Font Size">
                  <Button
                    onClick={() => {
                      const currentSize = parseInt(getCurrentFontSize(editor).replace('px', ''));
                      const newSize = Math.min(72, currentSize + 2) + 'px';
                      applyTypographyStyle(newSize);
                    }}
                    sx={{ px: 1 }}
                  >
                    <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>A+</Typography>
                  </Button>
                </Tooltip>
              </ButtonGroup>


              <Divider orientation="vertical" flexItem />

              {/* Lists */}
              <ButtonGroup size="small" variant="text" sx={{ mr: 0.5 }}>
                <Tooltip title={t('toolBulletList')}>
                  <Button
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    color={editor.isActive('bulletList') ? 'primary' : 'default'}
                    sx={{ px: 1 }}
                  >
                    <FormatListBulletedIcon fontSize="small" />
                  </Button>
                </Tooltip>
                <Tooltip title={t('toolNumberedList')}>
                  <Button
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    color={editor.isActive('orderedList') ? 'primary' : 'default'}
                    sx={{ px: 1 }}
                  >
                    <FormatListNumberedIcon fontSize="small" />
                  </Button>
                </Tooltip>
              </ButtonGroup>

              <Divider orientation="vertical" flexItem />

              {/* Alignment */}
              <ButtonGroup size="small" variant="text" sx={{ mr: 0.5 }}>
                <Tooltip title={t('toolAlignLeft')}>
                  <Button
                    onClick={() => editor.chain().focus().setTextAlign('left').run()}
                    color={editor.isActive({ textAlign: 'left' }) ? 'primary' : 'default'}
                    sx={{ px: 1 }}
                  >
                    <FormatAlignLeftIcon fontSize="small" />
                  </Button>
                </Tooltip>
                <Tooltip title={t('toolAlignCenter')}>
                  <Button
                    onClick={() => editor.chain().focus().setTextAlign('center').run()}
                    color={editor.isActive({ textAlign: 'center' }) ? 'primary' : 'default'}
                    sx={{ px: 1 }}
                  >
                    <FormatAlignCenterIcon fontSize="small" />
                  </Button>
                </Tooltip>
                <Tooltip title={t('toolAlignRight')}>
                  <Button
                    onClick={() => editor.chain().focus().setTextAlign('right').run()}
                    color={editor.isActive({ textAlign: 'right' }) ? 'primary' : 'default'}
                    sx={{ px: 1 }}
                  >
                    <FormatAlignRightIcon fontSize="small" />
                  </Button>
                </Tooltip>
                <Tooltip title={t('toolAlignJustify')}>
                  <Button
                    onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                    color={editor.isActive({ textAlign: 'justify' }) ? 'primary' : 'default'}
                    sx={{ px: 1 }}
                  >
                    <FormatAlignJustifyIcon fontSize="small" />
                  </Button>
                </Tooltip>
              </ButtonGroup>
            </Box>
            {canScrollRight && (
              <IconButton
                size="small"
                onClick={() => handleToolbarScroll('right')}
                sx={{
                  position: 'sticky',
                  right: 0,
                  zIndex: 2,
                  bgcolor: alpha(theme.palette.background.paper, 0.8),
                  '&:hover': { bgcolor: theme.palette.action.hover }
                }}
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      case 'Insert':
        return (
          <Box sx={{
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            overflowY: 'hidden',
            px: 1,
            alignItems: 'center',
            height: '100%',
            width: windowWidth / 2,
            flexWrap: 'nowrap',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
              display: 'none'
            }
          }}>
            {/* Image Upload */}
            <ButtonGroup size="small" variant="text" sx={{ mr: 0.5 }}>
              <Tooltip title={t('toolInsertImage')}>
                <Button onClick={() => imageInputRef.current.click()} sx={{ px: 1 }}>
                  <ImageIcon fontSize="small" />
                </Button>
              </Tooltip>
            </ButtonGroup>

            <Divider orientation="vertical" flexItem />

            {/* Insert Quote */}
            <ButtonGroup size="small" variant="text" sx={{ mr: 0.5 }}>
              <Tooltip title={t('toolInsertBlockquote')}>
                <Button
                  onClick={() => editor.chain().focus().toggleBlockquote().run()}
                  color={editor.isActive('blockquote') ? 'primary' : 'default'}
                  sx={{ px: 1 }}
                >
                  <FormatQuoteIcon fontSize="small" />
                </Button>
              </Tooltip>
            </ButtonGroup>

            {/* Insert Horizontal Rule */}
            <ButtonGroup size="small" variant="text" sx={{ mr: 0.5 }}>
              <Tooltip title={t('toolInsertHorizontalLine')}>
                <Button
                  onClick={() => editor.chain().focus().setHorizontalRule().run()}
                  sx={{ px: 1 }}
                >
                  <HorizontalRuleIcon fontSize="small" />
                </Button>
              </Tooltip>
            </ButtonGroup>

            <Divider orientation="vertical" flexItem />

            {/* Insert Page Break */}
            <ButtonGroup size="small" variant="text" sx={{ mr: 0.5 }}>
              <Tooltip title="Insert Page Break (Ctrl+Enter)">
                <Button
                  onClick={() => editor.chain().focus().insertPageBreak().run()}
                  sx={{ px: 1, gap: 0.5, fontSize: '0.75rem', textTransform: 'none' }}
                >
                  <PageIcon fontSize="small" />
                  Page Break
                </Button>
              </Tooltip>
            </ButtonGroup>
          </Box>
        );
      case 'Table':
        return (
          <Box sx={{
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            overflowY: 'hidden',
            px: 1,
            alignItems: 'center',
            height: '100%',
            width: windowWidth / 2,
            flexWrap: 'nowrap',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
              display: 'none'
            }
          }}>
            {/* Insert Table (always shown in Table menu) */}
            <ButtonGroup size="small" variant="text" sx={{ mr: 0.5 }}>
              <Tooltip title={t('toolInsertTable')}>
                <Button onClick={handleInsertTable} sx={{ px: 1 }}>
                  <TableChartIcon fontSize="small" />
                </Button>
              </Tooltip>
            </ButtonGroup>

            <Divider orientation="vertical" flexItem />

            {/* Table Operations - only shown if a table is selected */}
            {editor.isActive('table') ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', p: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
                  <Tooltip title={t('toolAddColumnBefore')}>
                    <Button
                      onClick={() => editor.chain().focus().addColumnBefore().run()}
                      sx={{ minWidth: 'auto', mx: 0.5 }}
                    >
                      <ColumnBeforeIcon />
                    </Button>
                  </Tooltip>

                  <Tooltip title={t('toolAddColumnAfter')}>
                    <Button
                      onClick={() => editor.chain().focus().addColumnAfter().run()}
                      sx={{ minWidth: 'auto', mx: 0.5 }}
                    >
                      <ColumnAfterIcon />
                    </Button>
                  </Tooltip>

                  <Tooltip title={t('toolAddRowBefore')}>
                    <Button
                      onClick={() => editor.chain().focus().addRowBefore().run()}
                      sx={{ minWidth: 'auto', mx: 0.5 }}
                    >
                      <RowAboveIcon />
                    </Button>
                  </Tooltip>

                  <Tooltip title={t('toolAddRowAfter')}>
                    <Button
                      onClick={() => editor.chain().focus().addRowAfter().run()}
                      sx={{ minWidth: 'auto', mx: 0.5 }}
                    >
                      <RowBelowIcon />
                    </Button>
                  </Tooltip>

                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                  <Tooltip title={t('toolDeleteRow')}>
                    <Button
                      onClick={() => editor.chain().focus().deleteRow().run()}
                      sx={{ minWidth: 'auto', mx: 0.5 }}
                    >
                      <DeleteRowIcon />
                    </Button>
                  </Tooltip>

                  <Tooltip title={t('toolDeleteColumn')}>
                    <Button
                      onClick={() => editor.chain().focus().deleteColumn().run()}
                      sx={{ minWidth: 'auto', mx: 0.5 }}
                    >
                      <DeleteColumnIcon />
                    </Button>
                  </Tooltip>

                  {/* <Divider orientation="vertical" flexItem sx={{ mx: 1 }} /> */}

                  {/* Add Table Cell Background Color Button */}
                  {/* <Tooltip title={t('toolTableCellBackground')}>
                    <Button
                      onClick={(e) => {
                        if (editor.isActive('tableCell')) {
                          handleSubmenuOpen(e, 'tableCellColor');
                        }
                      }}
                      sx={{ 
                        minWidth: 'auto', 
                        mx: 0.5,
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: 4,
                          left: 4,
                          right: 4,
                          height: 2,
                          backgroundColor: getTableCellBackground(editor) || 'transparent',
                          borderRadius: 1
                        }
                      }}
                    >
                      <FormatColorFillIcon fontSize="small" />
                    </Button>
                  </Tooltip> */}

                  <Button
                    onClick={() => editor.chain().focus().deleteTable().run()}
                    sx={{
                      color: 'error.main',
                      ml: 0.5,
                      '& .MuiSvgIcon-root': {
                        mr: 0.5,
                        color: 'error.main'
                      }
                    }}
                    startIcon={<DeleteIcon fontSize="small" />}
                  >
                    {t('toolDeleteTable')}
                  </Button>
                </Box>
              </>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', height: '100%', px: 2 }}>
                {t('toolTableOptions')}
              </Typography>
            )}
          </Box>
        );

      case 'PageLayout':
        return (
          <Box sx={{
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            overflowY: 'hidden',
            px: 1,
            alignItems: 'center',
            height: '100%',
            width: windowWidth / 2,
            flexWrap: 'nowrap',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' }
          }}>
            {/* Page Setup button — format, margins, orientation */}
            <ButtonGroup size="small" variant="text" sx={{ mr: 0.5 }}>
              <Tooltip title="Page Setup — format, margins, orientation">
                <Button
                  onClick={() => setPageSetupOpen(true)}
                  sx={{ px: 1.5, gap: 0.5, fontSize: '0.75rem', textTransform: 'none' }}
                >
                  <CropPortraitIcon fontSize="small" />
                  Page Setup
                </Button>
              </Tooltip>
            </ButtonGroup>
          </Box>
        );
      case 'Tools':
        return (
          <Box sx={{
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            overflowY: 'hidden',
            px: 1,
            alignItems: 'center',
            height: '100%',
            width: windowWidth / 2,
            flexWrap: 'nowrap',
            msOverflowStyle: 'none', /* for Internet Explorer, Edge */
            scrollbarWidth: 'none',  /* for Firefox */
            '&::-webkit-scrollbar': {
              display: 'none'  /* for Chrome, Safari, and Opera */
            }
          }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', height: '100%', px: 2 }}>
              Tools options will appear here
            </Typography>
          </Box>
        );
      case 'Page':
        return (
          <Box sx={{
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            overflowY: 'hidden',
            px: 1,
            alignItems: 'center',
            height: '100%',
            width: windowWidth / 2,
            flexWrap: 'nowrap',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' }
          }}>
            {/* Page Setup button */}
            <ButtonGroup size="small" variant="text" sx={{ mr: 0.5 }}>
              <Tooltip title="Page Setup — format, margins, header & footer">
                <Button
                  onClick={() => setPageSetupOpen(true)}
                  sx={{ px: 1.5, gap: 0.5, fontSize: '0.75rem', textTransform: 'none' }}
                >
                  <CropPortraitIcon fontSize="small" />
                  Page Setup
                </Button>
              </Tooltip>
            </ButtonGroup>

            <Divider orientation="vertical" flexItem />

            {/* Insert Page Break */}
            <ButtonGroup size="small" variant="text" sx={{ mr: 0.5 }}>
              <Tooltip title="Insert Page Break (Ctrl+Enter)">
                <Button
                  onClick={() => editor.chain().focus().insertPageBreak().run()}
                  sx={{ px: 1.5, gap: 0.5, fontSize: '0.75rem', textTransform: 'none' }}
                >
                  <PageIcon fontSize="small" />
                  Page Break
                </Button>
              </Tooltip>
            </ButtonGroup>
          </Box>
        );
      case 'Export':
        return (
          <Box sx={{
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            overflowY: 'hidden',
            px: 1,
            alignItems: 'center',
            height: '100%',
            width: windowWidth / 2,
            flexWrap: 'nowrap',
            msOverflowStyle: 'none', /* for Internet Explorer, Edge */
            scrollbarWidth: 'none',  /* for Firefox */
            '&::-webkit-scrollbar': {
              display: 'none'  /* for Chrome, Safari, and Opera */
            }
          }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', height: '100%', px: 2 }}>
              Export options will appear here
            </Typography>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Paper elevation={1} data-tour="editor-toolbar" sx={{ width: '100%', overflow: 'hidden', borderRadius: '4px', zIndex: 200000 }}>
        {/* First row: Back button, title, undo/redo, save */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.paper
          }}
        >
          <BackButton navigate_to={"/contract"} />

          {mode !== 'template' && contractId && (
            <Box sx={{ ml: 1 }}>
              <ContractLocationBreadcrumb
                contractId={contractId}
                workspaceId={contractWorkspaceId}
                folderId={contractFolderId}
                workspaceName={contractWorkspaceName}
                folderName={contractFolderName}
                onMoved={onContractMoved}
                variant="chip"
              />
            </Box>
          )}

          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 500,
              maxWidth: '400px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              marginLeft: 1.5
            }}
          >
            {contractTitle || 'Untitled Contract'}
          </Typography>

          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            {mode !== 'template' && (
              <CountryDropdown
                size="small"
                minWidth={{ xs: 120, md: 140 }}
                showLabel={false}
                disabled={isToolbarDisabled || !contractId}
                value={contractCountry || 'MA'}
                onChange={async (newCountry, previousCountry) => {
                  if (!contractId) return;
                  await dispatch(
                    updateContractCountry(contractId, newCountry, previousCountry)
                  );
                }}
                sx={{
                  mr: 1,
                  '& .MuiOutlinedInput-root': {
                    height: '32px',
                    '& .MuiSelect-select': {
                      padding: '4px 8px',
                      fontSize: '0.75rem'
                    }
                  }
                }}
              />
            )}
            {mode !== 'template' && (
              <ButtonGroup size="small" variant="text">
                <Tooltip title={t('toolContractStatus')}>
                  <Button
                    onClick={(e) => handleSubmenuOpen(e, 'contractStatus')}
                    disabled={isToolbarDisabled}
                    sx={{
                      textTransform: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    {loadingStatus ? (
                      <CircularProgress size={18} />
                    ) : (
                      <>
                        {contractStatus === 'DRAFT' && <EditNoteIcon fontSize="small" />}
                        {contractStatus === 'IN_REVIEW' && <PendingIcon fontSize="small" />}
                        {contractStatus === 'PENDING_SIGNATURE' && <SendTimeExtensionIcon fontSize="small" />}
                        {contractStatus === 'SIGNED' && <AssignmentTurnedInIcon fontSize="small" />}
                      </>
                    )}
                    <Typography variant="caption">
                      {t(`contractStatus.${contractStatus}`)}
                    </Typography>
                  </Button>
                </Tooltip>
              </ButtonGroup>
            )}

            <ButtonGroup size="small" variant="text">
              <Tooltip title={t('undo')}>
                <Button onClick={handleUndo} sx={{ px: 1 }} disabled={!editor || isToolbarDisabled}>
                  <UndoIcon fontSize="small" />
                </Button>
              </Tooltip>
              <Tooltip title={t('redo')}>
                <Button onClick={handleRedo} sx={{ px: 1 }} disabled={!editor || isToolbarDisabled}>
                  <RedoIcon fontSize="small" />
                </Button>
              </Tooltip>
            </ButtonGroup>

            <Tooltip title={hasUnsavedChanges ? t('saveChanges') : t('allChangesSaved')}>
              <Button
                variant="text"
                size="small"
                onClick={handleSave}
                disabled={isLocked}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  borderColor: hasUnsavedChanges ? 'warning.main' : 'success.main',
                  color: hasUnsavedChanges ? 'warning.main' : 'success.main',
                  // Hide save for reviewers — their suggestions are saved via socket, not manual save
                  ...(suggestModeLocked && { display: 'none' }),
                }}
              >
                {hasUnsavedChanges ? (
                  <>
                    <SaveIcon fontSize="small" />
                    <Typography variant="caption">{t('save')}</Typography>
                  </>
                ) : (
                  <>
                    <CheckCircleIcon fontSize="small" />
                    <Typography variant="caption">{t('saved')}</Typography>
                  </>
                )}
              </Button>
            </Tooltip>
          </Box>
        </Box>

        {/* Second row: Menu button, vertical divider, contextual actions */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.paper,
            height: '42px'
          }}
        >
          {/* Menu Button */}
          <Button
            ref={menuButtonRef}
            variant="text"
            onClick={handleMenuButtonClick}
            disabled={isToolbarDisabled}
            sx={{
              textTransform: 'none',
              color: theme.palette.text.primary,
              px: 2,
              height: '100%',
              borderRadius: 0,
              bgcolor: menuOpen ? theme.palette.action.selected : 'transparent',
              '&:hover': {
                bgcolor: theme.palette.action.hover
              }
            }}
            startIcon={
              activeMenu === 'Home' ? <HomeIcon fontSize="small" /> :
              activeMenu === 'Insert' ? <InsertDriveFileIcon fontSize="small" /> :
              activeMenu === 'Table' ? <TableChartIcon fontSize="small" /> :
              activeMenu === 'PageLayout' || activeMenu === 'Page' ? <PageIcon fontSize="small" /> :
              null
            }
            endIcon={menuOpen ? <KeyboardArrowUpIcon /> : <ArrowDropDownIcon />}
          >
            {activeMenu === 'Page' ? 'Page' : t(`tool${activeMenu}`)}
          </Button>

          {/* Vertical Divider */}
          <Divider orientation="vertical" flexItem />

          {/* Contextual Tools based on active menu */}
          <Box sx={{ flexGrow: 1, height: '100%', overflow: 'auto', ...(isToolbarDisabled && { pointerEvents: 'none', opacity: 0.4 }) }}>
            {renderToolbar()}
          </Box>

          {/* Vertical Divider */}
          <Divider orientation="vertical" flexItem />

          {mode !== 'template' && (
            <Box sx={{ px: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Review mode chip — shown when suggest mode is active */}
              {suggestModeActive && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    border: '1px solid',
                    borderColor: 'warning.main',
                    borderRadius: '24px',
                    px: 1.5,
                    py: 0.5,
                    userSelect: 'none',
                  }}
                >
                  <EditIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                  <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.primary', whiteSpace: 'nowrap' }}>
                    {t('suggest_mode')}
                  </Typography>
                  {!suggestModeLocked && (
                    <IconButton
                      size="small"
                      onClick={() => onSuggestModeChange?.(false)}
                      sx={{ p: 0, width: 18, height: 18, color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
                    >
                      <CloseIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  )}
                </Box>
              )}

              {/* AI Toolbar Button */}
              <AiToolbarButton ref={aiButtonRef} editor={editor} contractId={contractId} />

              {/* Suggest Mode Button */}
              {!isLocked && (
                <SuggestModeButton
                  active={suggestModeActive}
                  onClick={suggestModeLocked ? undefined : handleSuggestModeToggle}
                  disabled={suggestModeLocked}
                />
              )}
            </Box>
          )}

          {/* Hidden file input for image uploads */}
          <input
            type="file"
            ref={imageInputRef}
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleImageUpload}
          />
        </Box>
      </Paper>

      {/* Menu Component */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={() => handleMenuItemClick('Home')}>
          <ListItemIcon>
            <HomeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('toolHome')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('Insert')}>
          <ListItemIcon>
            <InsertDriveFileIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('toolInsert')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('Table')}>
          <ListItemIcon>
            <TableChartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('toolTable')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('Page')}>
          <ListItemIcon>
            <PageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Page</ListItemText>
        </MenuItem>
      </Menu>

      {/* Page Setup dialog */}
      {pageSettings && (
        <PageSetupPanel
          open={pageSetupOpen}
          onClose={() => setPageSetupOpen(false)}
          pageSettings={pageSettings}
          onApply={(newSettings) => onPageSettingsChange && onPageSettingsChange(newSettings)}
        />
      )}

      {/* Suggest mode button (rendered into toolbar via portal or placed here) */}

      <Popover 
        open={tableDialogOpen} 
        onClose={handleTableDialogClose}
        anchorReference="anchorPosition"
        anchorPosition={{
          top: mousePosition.y,
          left: mousePosition.x
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            boxShadow: 3
          }
        }}
      >
        <TableGridSelection />
      </Popover>

      {/* Update Popover for submenus */}
      <Popover
        open={Boolean(submenuAnchorEl)}
        anchorEl={submenuAnchorEl}
        onClose={handleSubmenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 0.5,
            boxShadow: 3,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1,
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }
          }
        }}
      >
        {currentSubmenu === 'heading' && <HeadingSubmenu />}
        {currentSubmenu === 'textColor' && <TextColorSubmenu />}
        {currentSubmenu === 'tableCellColor' && <TableCellColorSubmenu />}
        {currentSubmenu === 'contractStatus' && (
          <ContractStatusSubmenu
            onStatusChange={handleContractStatusChange}
            currentStatus={contractStatus}
            isLocked={isToolbarDisabled}
          />
        )}
      </Popover>
    </>
  );
};

export default ToolbarPlugin;